import { initializeApp, type FirebaseOptions } from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  getMultiFactorResolver,
  getRedirectResult,
  isSignInWithEmailLink,
  multiFactor,
  OAuthProvider,
  SAMLAuthProvider,
  sendSignInLinkToEmail,
  setPersistence,
  signInWithEmailLink,
  signInWithRedirect,
  signOut,
  TotpMultiFactorGenerator,
  verifyBeforeUpdateEmail,
  type Auth,
  type MultiFactorError,
  type MultiFactorUser,
  type User,
} from 'firebase/auth';

const EMAIL_FOR_SIGN_IN = 'apicircle.firebase.emailForSignIn';
const TOTP_DISPLAY_NAME = 'Authenticator app';

export type FirebaseAuthResult =
  | { status: 'idle' }
  | { status: 'email_sent'; email: string }
  | { status: 'email_link_email_required' }
  | { status: 'mfa_required' }
  | { status: 'totp_enrollment_required' }
  | { status: 'signed_in'; idToken: string };

export interface TotpEnrollment {
  qrCodeUrl: string;
  secretKey: string;
}

export interface AccountFirebaseAuth {
  sendEmailLink(email: string): Promise<FirebaseAuthResult>;
  completeEmailLink(url: string, email?: string): Promise<FirebaseAuthResult>;
  completeRedirect(): Promise<FirebaseAuthResult>;
  beginTotpEnrollment(): Promise<TotpEnrollment>;
  finishTotpEnrollment(code: string): Promise<FirebaseAuthResult>;
  finishTotpSignIn(code: string): Promise<FirebaseAuthResult>;
  startSso(providerId: string): Promise<void>;
  currentIdToken(): Promise<string | null>;
  requestEmailUpdate(email: string): Promise<void>;
  signOut(): Promise<void>;
}

export interface FirebaseAccountConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  emulatorHost?: string;
  signInUrl?: string;
}

function hasTotp(user: User): boolean {
  return multiFactor(user).enrolledFactors.some((factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID);
}

function isMfaError(err: unknown): err is MultiFactorError {
  return typeof err === 'object' && err !== null && (err as { code?: unknown }).code === 'auth/multi-factor-auth-required';
}

function cleanSignInUrl(url: string): string {
  const parsed = new URL(url);
  parsed.search = '';
  parsed.hash = '';
  return parsed.toString();
}

function emailFromStorage(storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>): string | null {
  return storage.getItem(EMAIL_FOR_SIGN_IN);
}

export function createFirebaseAccountAuth(
  cfg: FirebaseAccountConfig,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = window.localStorage,
): AccountFirebaseAuth {
  const options: FirebaseOptions = {
    apiKey: cfg.apiKey,
    authDomain: cfg.authDomain,
    projectId: cfg.projectId,
    appId: cfg.appId,
  };
  const app = initializeApp(options);
  const auth = getAuth(app);
  void setPersistence(auth, browserLocalPersistence);
  if (cfg.emulatorHost) connectAuthEmulator(auth, cfg.emulatorHost, { disableWarnings: true });
  return firebaseAuthFromAuth(auth, storage, cfg.signInUrl);
}

export function firebaseAuthFromAuth(
  auth: Auth,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
  signInUrl = window.location.origin + window.location.pathname,
): AccountFirebaseAuth {
  let pendingResolver: ReturnType<typeof getMultiFactorResolver> | null = null;
  let pendingTotpSecret: Awaited<ReturnType<typeof TotpMultiFactorGenerator.generateSecret>> | null = null;

  async function finishUser(user: User): Promise<FirebaseAuthResult> {
    if (!hasTotp(user)) return { status: 'totp_enrollment_required' };
    return { status: 'signed_in', idToken: await user.getIdToken(true) };
  }

  async function withMfaHandling(fn: () => Promise<{ user: User } | null>): Promise<FirebaseAuthResult> {
    try {
      const result = await fn();
      return result?.user ? finishUser(result.user) : { status: 'idle' };
    } catch (err) {
      if (!isMfaError(err)) throw err;
      pendingResolver = getMultiFactorResolver(auth, err);
      return { status: 'mfa_required' };
    }
  }

  return {
    async sendEmailLink(email) {
      const normalized = email.trim().toLowerCase();
      await sendSignInLinkToEmail(auth, normalized, {
        url: cleanSignInUrl(signInUrl),
        handleCodeInApp: true,
      });
      storage.setItem(EMAIL_FOR_SIGN_IN, normalized);
      return { status: 'email_sent', email: normalized };
    },

    async completeEmailLink(url, emailOverride) {
      if (!isSignInWithEmailLink(auth, url)) return { status: 'idle' };
      const email = emailOverride?.trim().toLowerCase() || emailFromStorage(storage);
      if (!email) return { status: 'email_link_email_required' };
      const result = await withMfaHandling(() => signInWithEmailLink(auth, email, url));
      if (result.status !== 'mfa_required') storage.removeItem(EMAIL_FOR_SIGN_IN);
      return result;
    },

    async completeRedirect() {
      return withMfaHandling(() => getRedirectResult(auth));
    },

    async beginTotpEnrollment() {
      const user = auth.currentUser;
      if (!user) throw new Error('not_signed_in');
      const mfaUser: MultiFactorUser = multiFactor(user);
      pendingTotpSecret = await TotpMultiFactorGenerator.generateSecret(await mfaUser.getSession());
      return {
        qrCodeUrl: pendingTotpSecret.generateQrCodeUrl(user.email ?? 'API Circle account', 'API Circle'),
        secretKey: pendingTotpSecret.secretKey,
      };
    },

    async finishTotpEnrollment(code) {
      const user = auth.currentUser;
      if (!user || !pendingTotpSecret) throw new Error('totp_enrollment_not_started');
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(pendingTotpSecret, code.trim());
      await multiFactor(user).enroll(assertion, TOTP_DISPLAY_NAME);
      pendingTotpSecret = null;
      return finishUser(user);
    },

    async finishTotpSignIn(code) {
      if (!pendingResolver) throw new Error('mfa_challenge_not_started');
      const hint = pendingResolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
      if (!hint) throw new Error('totp_factor_not_available');
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.trim());
      const result = await pendingResolver.resolveSignIn(assertion);
      pendingResolver = null;
      return finishUser(result.user);
    },

    async startSso(providerId) {
      const provider = providerId.startsWith('saml.') ? new SAMLAuthProvider(providerId) : new OAuthProvider(providerId);
      await signInWithRedirect(auth, provider);
    },

    currentIdToken() {
      return auth.currentUser?.getIdToken(true) ?? Promise.resolve(null);
    },

    async requestEmailUpdate(email) {
      const user = auth.currentUser;
      if (!user) throw new Error('not_signed_in');
      await verifyBeforeUpdateEmail(user, email.trim().toLowerCase(), {
        url: cleanSignInUrl(signInUrl),
        handleCodeInApp: true,
      });
    },

    signOut: () => signOut(auth),
  };
}

export function firebaseConfigFromEnv(env: Record<string, string | undefined> = import.meta.env): FirebaseAccountConfig | null {
  const apiKey = env.VITE_FIREBASE_API_KEY;
  const authDomain = env.VITE_FIREBASE_AUTH_DOMAIN;
  const projectId = env.VITE_FIREBASE_PROJECT_ID;
  const appId = env.VITE_FIREBASE_APP_ID;
  if (!apiKey || !authDomain || !projectId || !appId) return null;
  return {
    apiKey,
    authDomain,
    projectId,
    appId,
    emulatorHost: env.VITE_FIREBASE_AUTH_EMULATOR_HOST,
  };
}
